import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import {
  ExamAttemptForbiddenError,
  ExamAttemptNotFoundError,
  getAttemptDetail,
  type ExamHistoryEntry,
} from "@/lib/exam-api";

/**
 * apps/web :: pages :: ExamAttemptDetailPage
 *
 * ROADS-430 AC #4 — the click-through detail view for a single past
 * attempt. Mounted at `/exam/history/:attemptId` under
 * <ProtectedRoute><OnboardingGate>.
 *
 * Hydration model (ROADS-450 widening):
 *   1. **Router-state fast path.** When the user clicks a row in
 *      ExamHistoryPage, the row passes `location.state.attempt` so
 *      this page renders synchronously with no extra network call.
 *   2. **Deep-link / refresh fallback.** When `location.state` is
 *      missing — page-refresh, share-link, browser-history nav — we
 *      fetch GET /api/exam-attempts/:id (ROADS-450) and render once
 *      the response lands.
 *   3. **403 / 404 / network errors** each surface a friendly,
 *      AT-announced (`role="alert"`) copy.
 *
 * The page reuses the same color-blind-safe pass/fail vocabulary
 * as ExamHistoryPage and ExamResultsCard.
 *
 * Why we don't reuse ExamResultsCard wholesale:
 *   ExamResultsCard requires `SubmitExamResponse` (with the full
 *   per-question review + breakdown) AND the `ExamQuestionForClient[]`
 *   that drove the attempt. Neither is available from the history
 *   endpoint. Surfacing a stub that mimics the post-exam shape would
 *   mislead the learner ("you got 3/10 in Right-of-Way" without the
 *   evidence that produced that bucket). The fullReviewNote line
 *   below tells the user the per-question review only renders
 *   immediately after submission.
 */

/** Internal display shape — common to router-state and fetched payloads. */
interface DetailViewModel {
  attemptId: string;
  stateCode: string;
  passed: boolean;
  correctCount: number;
  questionCount: number;
  submittedAtMs: number;
  timeSpentMs: number;
  attemptNumber: number;
}

interface AttemptLocationState {
  attempt: ExamHistoryEntry & { stateCode: string };
}

function isAttemptLocationState(s: unknown): s is AttemptLocationState {
  if (typeof s !== "object" || s === null) return false;
  const candidate = (s as { attempt?: unknown }).attempt;
  if (typeof candidate !== "object" || candidate === null) return false;
  const a = candidate as Record<string, unknown>;
  return (
    typeof a.attemptId === "string" &&
    typeof a.submittedAtMs === "number" &&
    typeof a.passed === "boolean" &&
    typeof a.correctCount === "number" &&
    typeof a.questionCount === "number" &&
    typeof a.stateCode === "string"
  );
}

type FetchState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; vm: DetailViewModel }
  | { kind: "forbidden" }
  | { kind: "not-found" }
  | { kind: "error" };

function formatDate(ms: number, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString().slice(0, 16).replace("T", " ");
  }
}

function formatTimeSpent(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1_000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s}s`;
}

export default function ExamAttemptDetailPage() {
  const { t, i18n } = useTranslation();
  const { attemptId } = useParams<{ attemptId: string }>();
  const location = useLocation();
  const state = location.state;
  const routerStateAttempt = isAttemptLocationState(state) ? state.attempt : null;

  // Router-state fast path: if the row click handed us the summary AND the
  // id matches the URL, we render synchronously with no fetch. The id-match
  // gate defends against router-state staleness (back-nav landing on a
  // different attempt's URL but with stale state).
  const initialFromRouter: DetailViewModel | null =
    routerStateAttempt && routerStateAttempt.attemptId === attemptId
      ? routerStateAttempt
      : null;

  const [fetchState, setFetchState] = useState<FetchState>(
    initialFromRouter ? { kind: "success", vm: initialFromRouter } : { kind: "idle" },
  );

  useEffect(() => {
    // Skip the fetch when router-state already populated the view.
    if (initialFromRouter) return;
    if (!attemptId) {
      setFetchState({ kind: "not-found" });
      return;
    }
    let cancelled = false;
    setFetchState({ kind: "loading" });
    void getAttemptDetail(attemptId)
      .then((detail) => {
        if (cancelled) return;
        setFetchState({
          kind: "success",
          vm: {
            attemptId: detail.id,
            stateCode: detail.stateCode,
            passed: detail.passed,
            correctCount: detail.score,
            questionCount: detail.questionCount,
            submittedAtMs: detail.completedAt,
            // started_at + completed_at give us the client-side time-spent
            // (the wire shape doesn't carry timeSpentMs on the detail
            // route — keep that derivable so the existing summary cell
            // just works).
            timeSpentMs: Math.max(0, detail.completedAt - detail.startedAt),
            // The detail endpoint does not surface attempt_number (the
            // history list does). Default to 1 so the row renders
            // deterministically; UX-wise the "Attempt #" cell is a
            // weak signal anyway and the per-question review note
            // points users at the next attempt for fresh diagnostics.
            attemptNumber: 1,
          },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ExamAttemptForbiddenError) {
          setFetchState({ kind: "forbidden" });
        } else if (err instanceof ExamAttemptNotFoundError) {
          setFetchState({ kind: "not-found" });
        } else {
          setFetchState({ kind: "error" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attemptId, initialFromRouter]);

  return (
    <LearnerPortalShell pageTitle={t("examHistory.detail.title")}>
      <section
        className="mx-auto max-w-4xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl"
        data-testid="exam-attempt-detail-page"
      >
        <header className="flex flex-col gap-xs">
          <p className="text-sm font-semibold uppercase tracking-wide text-neutral-600">
            {t("examHistory.detail.eyebrow")}
          </p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl">
            {t("examHistory.detail.title")}
          </h1>
          <Link
            to="/exam/history"
            data-testid="exam-attempt-detail-back-link"
            className="mt-xs text-sm font-semibold text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            ← {t("examHistory.detail.backLink")}
          </Link>
        </header>

        <DetailSurface fetchState={fetchState} locale={i18n.language} />
      </section>
    </LearnerPortalShell>
  );
}

function DetailSurface({
  fetchState,
  locale,
}: {
  fetchState: FetchState;
  locale: string;
}) {
  const { t } = useTranslation();

  if (fetchState.kind === "loading" || fetchState.kind === "idle") {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="exam-attempt-detail-loading"
        className="mt-lg rounded-lg border border-brand-primary/10 bg-white p-md text-sm text-neutral-700 shadow-sm sm:p-lg"
      >
        {t("examHistory.detail.loading")}
      </div>
    );
  }

  if (fetchState.kind === "forbidden") {
    return (
      <div
        role="alert"
        data-testid="exam-attempt-detail-forbidden"
        className="mt-lg rounded-lg border border-amber-500/40 bg-amber-500/5 p-md text-sm text-amber-800 shadow-sm sm:p-lg"
      >
        {t("examHistory.detail.forbidden")}
      </div>
    );
  }

  if (fetchState.kind === "not-found") {
    return (
      <div
        role="alert"
        data-testid="exam-attempt-detail-not-found"
        className="mt-lg rounded-lg border border-amber-500/40 bg-amber-500/5 p-md text-sm text-amber-800 shadow-sm sm:p-lg"
      >
        {t("examHistory.detail.notFound")}
      </div>
    );
  }

  if (fetchState.kind === "error") {
    return (
      <div
        role="alert"
        data-testid="exam-attempt-detail-error"
        className="mt-lg rounded-lg border border-amber-500/40 bg-amber-500/5 p-md text-sm text-amber-800 shadow-sm sm:p-lg"
      >
        {t("examHistory.detail.networkError")}
      </div>
    );
  }

  return <DetailBody attempt={fetchState.vm} locale={locale} />;
}

function DetailBody({
  attempt,
  locale,
}: {
  attempt: DetailViewModel;
  locale: string;
}) {
  const { t } = useTranslation();
  const status = attempt.passed ? "pass" : "fail";
  const badgeLabel = attempt.passed
    ? t("examHistory.badge.pass")
    : t("examHistory.badge.fail");
  const badgeClass = attempt.passed
    ? "border-success/40 bg-success/10 text-success"
    : "border-amber-500/40 bg-amber-500/10 text-amber-700";
  const percent = Math.round(
    (attempt.correctCount / Math.max(1, attempt.questionCount)) * 100,
  );

  return (
    <div className="mt-lg flex flex-col gap-md">
      <div
        data-testid="exam-attempt-detail-banner"
        className={`flex items-center justify-between gap-sm rounded-lg border p-md shadow-sm sm:p-lg ${badgeClass}`}
      >
        <div className="flex items-center gap-sm">
          <span aria-hidden="true" className="text-2xl font-bold">
            {attempt.passed ? "✓" : "✕"}
          </span>
          <div>
            <h2 className="font-display text-xl font-bold uppercase tracking-wide">
              {badgeLabel}
            </h2>
            <p
              data-testid="exam-attempt-detail-score"
              data-status={status}
              className="mt-xs text-sm"
            >
              {t("examHistory.score", {
                correct: attempt.correctCount,
                total: attempt.questionCount,
              })}{" "}
              ({percent}%)
            </p>
          </div>
        </div>
      </div>

      <section
        data-testid="exam-attempt-detail-summary"
        aria-labelledby="exam-attempt-summary-heading"
        className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
      >
        <h3
          id="exam-attempt-summary-heading"
          className="font-display text-lg font-bold text-brand-primary"
        >
          {t("examHistory.detail.summaryHeading")}
        </h3>
        <dl className="mt-md grid grid-cols-1 gap-sm text-sm sm:grid-cols-2">
          <SummaryRow
            label={t("examHistory.detail.stateLabel")}
            value={attempt.stateCode}
            testId="exam-attempt-detail-state"
          />
          <SummaryRow
            label={t("examHistory.detail.dateLabel")}
            value={formatDate(attempt.submittedAtMs, locale)}
            testId="exam-attempt-detail-date"
          />
          <SummaryRow
            label={t("examHistory.detail.scoreLabel")}
            value={t("examHistory.score", {
              correct: attempt.correctCount,
              total: attempt.questionCount,
            })}
            testId="exam-attempt-detail-score-row"
          />
          <SummaryRow
            label={t("examHistory.detail.timeLabel")}
            value={formatTimeSpent(attempt.timeSpentMs)}
            testId="exam-attempt-detail-time"
          />
          <SummaryRow
            label={t("examHistory.detail.resultLabel")}
            value={badgeLabel}
            testId="exam-attempt-detail-result"
          />
          <SummaryRow
            label={t("examHistory.detail.attemptNumberLabel")}
            value={String(attempt.attemptNumber)}
            testId="exam-attempt-detail-attempt-number"
          />
        </dl>
      </section>

      <p
        data-testid="exam-attempt-detail-full-review-note"
        className="text-xs text-neutral-600"
      >
        {t("examHistory.detail.fullReviewNote")}
      </p>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-sm border-b border-brand-primary/5 py-xs last:border-b-0 sm:border-b-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
        {label}
      </dt>
      <dd
        data-testid={testId}
        className="text-right font-semibold text-brand-primary"
      >
        {value}
      </dd>
    </div>
  );
}
